/**
 * Manages timing constraints for AI move selection
 * Ensures minimum viewing delay and maximum thinking time
 */

export interface TimeConstraints {
  minDelay: number; // Minimum delay for human viewing (1000ms)
  maxThinkingTime: number; // Maximum AI thinking time (12000ms)
}

export class TimeManager {
  private static readonly DEFAULT_MIN_DELAY = 1000; // 1 second minimum
  private static readonly DEFAULT_MAX_THINKING = 12000; // 12 seconds maximum

  /**
   * Execute an AI's move selection with proper timing constraints
   * @param aiFunction The AI's move selection function
   * @param constraints Timing constraints to enforce
   * @returns Promise that resolves with the move result after proper delay
   */
  static async executeWithTiming<T>(
    aiFunction: () => Promise<T>,
    constraints: TimeConstraints = {
      minDelay: TimeManager.DEFAULT_MIN_DELAY,
      maxThinkingTime: TimeManager.DEFAULT_MAX_THINKING,
    }
  ): Promise<T> {
    const startTime = Date.now();

    // Set up timeout for maximum thinking time
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`AI exceeded maximum thinking time of ${constraints.maxThinkingTime}ms`));
      }, constraints.maxThinkingTime);
    });

    try {
      // Race between AI function and timeout
      const result = await Promise.race([aiFunction(), timeoutPromise]);
      const elapsedTime = Date.now() - startTime;

      // If AI finished quickly, wait until minimum delay
      if (elapsedTime < constraints.minDelay) {
        const remainingDelay = constraints.minDelay - elapsedTime;
        await new Promise((resolve) => setTimeout(resolve, remainingDelay));
      }

      return result;
    } catch (error) {
      // If timeout occurred, handle gracefully
      throw error;
    }
  }

  /**
   * Create a time-limited version of an async function
   * @param maxTime Maximum time in milliseconds
   */
  static createTimeLimitedFunction<T>(
    fn: () => Promise<T>,
    maxTime: number
  ): () => Promise<T> {
    return async () => {
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Function exceeded time limit of ${maxTime}ms`));
        }, maxTime);
      });

      return Promise.race([fn(), timeoutPromise]);
    };
  }

  /**
   * Check if enough time has elapsed for minimum delay
   * @param startTime Start timestamp
   * @param minDelay Minimum required delay
   * @returns Remaining delay time in ms (0 if already exceeded)
   */
  static getRemainingDelay(startTime: number, minDelay: number): number {
    const elapsed = Date.now() - startTime;
    return Math.max(0, minDelay - elapsed);
  }

  /**
   * Create a promise that resolves after the minimum delay
   * considering time already elapsed
   * @param startTime When the operation started
   * @param minDelay Minimum total delay required
   */
  static async ensureMinimumDelay(startTime: number, minDelay: number): Promise<void> {
    const remaining = TimeManager.getRemainingDelay(startTime, minDelay);
    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining));
    }
  }

  /**
   * Format time duration for display
   * @param ms Time in milliseconds
   * @returns Formatted string (e.g., "1.5s", "250ms")
   */
  static formatDuration(ms: number): string {
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    return `${Math.round(ms)}ms`;
  }
}